import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Mail, Search, Filter, Eye, Send, CheckCircle2, Clock, Archive } from 'lucide-react';
import { toast } from 'sonner';

interface ContactMessage {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  category: string;
  status: string;
  admin_response: string | null;
  responded_at: string | null;
  responded_by: string | null;
  created_at: string;
}

const ContactManagement = () => {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);
  const [responseText, setResponseText] = useState('');
  const [isResponding, setIsResponding] = useState(false);

  const statusOptions = [
    { value: 'all', label: 'Todos los Estados' },
    { value: 'pending', label: 'Pendientes' },
    { value: 'in_progress', label: 'En Proceso' },
    { value: 'resolved', label: 'Resueltos' },
    { value: 'archived', label: 'Archivados' }
  ];

  const categoryOptions = [
    { value: 'all', label: 'Todas las Categorías' },
    { value: 'general', label: 'General' },
    { value: 'curso', label: 'Cursos' },
    { value: 'soporte_tecnico', label: 'Soporte Técnico' },
    { value: 'colaboracion', label: 'Colaboración' },
    { value: 'prensa', label: 'Prensa' }
  ];

  useEffect(() => {
    loadMessages();
  }, [statusFilter, categoryFilter]);

  const loadMessages = async () => {
    try {
      let query = supabase
        .from('contact_messages')
        .select('*')
        .order('created_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      if (categoryFilter !== 'all') {
        query = query.eq('category', categoryFilter);
      }

      const { data, error } = await query;

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      console.error('Error al cargar mensajes:', error);
      toast.error('Error al cargar los mensajes');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (messageId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('contact_messages')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', messageId);

      if (error) throw error;

      toast.success('Estado actualizado correctamente');
      loadMessages();
    } catch (error) {
      console.error('Error al actualizar estado:', error);
      toast.error('Error al actualizar el estado');
    }
  };

  const handleSendResponse = async () => {
    if (!selectedMessage || !responseText.trim()) {
      toast.error('Por favor, escribe una respuesta');
      return;
    }

    setIsResponding(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('contact_messages')
        .update({
          admin_response: responseText.trim(),
          responded_at: new Date().toISOString(),
          responded_by: user?.id,
          status: 'resolved',
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedMessage.id);

      if (error) throw error;

      toast.success('Respuesta enviada correctamente');
      setResponseText('');
      setSelectedMessage(null);
      loadMessages();
    } catch (error) {
      console.error('Error al enviar respuesta:', error);
      toast.error('Error al enviar la respuesta');
    } finally {
      setIsResponding(false);
    }
  };

  const filteredMessages = messages.filter(msg =>
    msg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    msg.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    msg.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
    msg.message.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { color: string; label: string; icon: any }> = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pendiente', icon: Clock },
      in_progress: { color: 'bg-blue-100 text-blue-800', label: 'En Proceso', icon: Eye },
      resolved: { color: 'bg-green-100 text-green-800', label: 'Resuelto', icon: CheckCircle2 },
      archived: { color: 'bg-gray-100 text-gray-800', label: 'Archivado', icon: Archive }
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      general: 'General',
      curso: 'Cursos',
      soporte_tecnico: 'Soporte Técnico',
      colaboracion: 'Colaboración',
      prensa: 'Prensa'
    };
    return labels[category] || category;
  };

  const stats = {
    total: messages.length,
    pending: messages.filter(m => m.status === 'pending').length,
    inProgress: messages.filter(m => m.status === 'in_progress').length,
    resolved: messages.filter(m => m.status === 'resolved').length
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestión de Mensajes de Contacto</h1>
        <p className="text-gray-600">Administra y responde los mensajes recibidos desde el formulario de contacto</p>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 mb-1">Total de Mensajes</p>
          <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-yellow-50 rounded-lg shadow p-6">
          <p className="text-sm text-yellow-800 mb-1">Pendientes</p>
          <p className="text-3xl font-bold text-yellow-900">{stats.pending}</p>
        </div>
        <div className="bg-blue-50 rounded-lg shadow p-6">
          <p className="text-sm text-blue-800 mb-1">En Proceso</p>
          <p className="text-3xl font-bold text-blue-900">{stats.inProgress}</p>
        </div>
        <div className="bg-green-50 rounded-lg shadow p-6">
          <p className="text-sm text-green-800 mb-1">Resueltos</p>
          <p className="text-3xl font-bold text-green-900">{stats.resolved}</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por nombre, email o contenido..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {categoryOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Lista de Mensajes */}
      <div className="bg-white rounded-lg shadow-md">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando mensajes...</p>
          </div>
        ) : filteredMessages.length === 0 ? (
          <div className="p-12 text-center">
            <Mail className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay mensajes</h3>
            <p className="text-gray-600">
              {searchTerm ? 'No se encontraron mensajes con ese criterio de búsqueda' : 'Aún no hay mensajes de contacto'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredMessages.map((message) => (
              <div key={message.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {message.subject}
                    </h3>
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <span className="font-medium">{message.name}</span>
                      <span>•</span>
                      <span>{message.email}</span>
                      <span>•</span>
                      <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                        {getCategoryLabel(message.category)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(message.status)}
                  </div>
                </div>

                <p className="text-gray-700 mb-4 line-clamp-2">{message.message}</p>

                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    Recibido el {new Date(message.created_at).toLocaleString('es-ES')}
                  </p>

                  <div className="flex gap-2">
                    {message.status !== 'resolved' && (
                      <>
                        <button
                          onClick={() => handleUpdateStatus(message.id, 'in_progress')}
                          className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                        >
                          En Proceso
                        </button>
                        <button
                          onClick={() => {
                            setSelectedMessage(message);
                            setResponseText(message.admin_response || '');
                          }}
                          className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors flex items-center gap-1"
                        >
                          <Send className="w-4 h-4" />
                          Responder
                        </button>
                      </>
                    )}
                    {message.status === 'resolved' && (
                      <button
                        onClick={() => handleUpdateStatus(message.id, 'archived')}
                        className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        Archivar
                      </button>
                    )}
                  </div>
                </div>

                {message.admin_response && (
                  <div className="mt-4 p-4 bg-green-50 border-l-4 border-green-500 rounded">
                    <p className="text-sm font-semibold text-green-900 mb-1">Respuesta enviada:</p>
                    <p className="text-sm text-green-800">{message.admin_response}</p>
                    {message.responded_at && (
                      <p className="text-xs text-green-600 mt-2">
                        Respondido el {new Date(message.responded_at).toLocaleString('es-ES')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de Respuesta */}
      {selectedMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Responder Mensaje</h2>
            </div>

            <div className="p-6">
              {/* Información del Mensaje Original */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">{selectedMessage.subject}</h3>
                <p className="text-sm text-gray-600 mb-3">
                  De: {selectedMessage.name} ({selectedMessage.email})
                </p>
                <p className="text-gray-700">{selectedMessage.message}</p>
              </div>

              {/* Campo de Respuesta */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tu Respuesta
                </label>
                <textarea
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  rows={8}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Escribe tu respuesta aquí..."
                />
              </div>

              {/* Botones */}
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setSelectedMessage(null);
                    setResponseText('');
                  }}
                  className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSendResponse}
                  disabled={isResponding || !responseText.trim()}
                  className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isResponding ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Enviar Respuesta
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactManagement;
